import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavBar } from 'components/NavBar/NavBar'
import { toast } from 'react-toastify'
import { RootState } from 'store'
import {
    Flex,
    MainHeadline,
    PageFooter,
    PageWrapper,
} from 'styling/GlobalStyles'
import { OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { Pagination } from 'components/Pagination/Pagination'
import { formatNumbersForDisplay, getParams, handlePagination } from 'utils/helpers'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import useQueryPagination from 'hooks/useQueryPagination'
import {
    approveFuelLossWriteOff,
    clearFuelLosses,
    getFuelLosses,
    updateFuelLossStatus,
} from 'features/fuelManagement/fuelManagementSlice'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { AlarmBanner, AlarmContent, AlarmDot, AlarmText } from 'pages/Operations/Safety/Safety.styled'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { IFuelOpsLossRow } from 'types/fuelOps.interface'
import { FuelLossCaseStatus, FuelLossOutcome } from 'types/fuelEnums.interface'
import { mapFuelLoss } from 'pages/FuelManagement/Shared/fuelApiMappers'
import {
    criticalLossBannerText,
    findOpenCriticalLoss,
    formatGhs,
    fuelLossCostGhs,
    FUEL_LOSS_CLOSURE_CAPTION,
    FUEL_RESIDUAL_EXPLAINER,
} from '../Shared/fuelFormatters'
import { LossCellEm, LossExplainerWrap, LossInvestigationCell } from './FuelLosses.styled'
import { CASE_STATUS_META, LossCaseModal, OUTCOME_META } from './LossCaseModal'

const STATUS_TONE: { [k in IFuelOpsLossRow['status']]: IStatusPill['status'] } = {
    'Under investigation': 'danger',
    'Explained': 'success',
    'Written off — signed': 'neutral',
}

const columns: IHonestTableColumn<IFuelOpsLossRow>[] = [
    { id: 'date', header: 'DATE', cell: (item) => item.dateLabel },
    {
        id: 'severity',
        header: 'SEVERITY',
        cell: (item) => (
            <StatusPill status={item.severity === 'critical' ? 'danger' : 'warning'} dot={item.severity === 'critical'}>
                {item.severity === 'critical' ? 'Critical' : 'Warning'}
            </StatusPill>
        ),
    },
    {
        id: 'amount',
        header: 'AMOUNT',
        cell: (item) => <LossCellEm>{`${formatNumbersForDisplay(item.liters)} L`}</LossCellEm>,
    },
    {
        id: 'cost',
        header: 'COST',
        cell: (item) => (item.outcome === 'RECOVERED'
            ? <StatusPill status='success'>Recovered</StatusPill>
            : (
                <StatusPill status={item.severity === 'critical' ? 'danger' : 'neutral'}>
                    {formatGhs(item.costGhs ?? fuelLossCostGhs(item.liters))}
                </StatusPill>
            )),
    },
    { id: 'tank', header: 'TANK', cell: (item) => item.tank },
    { id: 'classification', header: 'CLASSIFICATION', cell: (item) => item.classification },
    { id: 'owner', header: 'OWNER', cell: (item) => item.owner },
    {
        id: 'status',
        header: 'STATUS',
        cell: (item) => {
            const meta = CASE_STATUS_META[item.caseStatus || 'OPEN']
            return (
                <StatusPill status={meta.tone} dot={item.caseStatus === 'PENDING_APPROVAL'}>
                    {meta.label}
                </StatusPill>
            )
        },
    },
    {
        id: 'outcome',
        header: 'OUTCOME',
        cell: (item) => (item.outcome
            ? <StatusPill status={OUTCOME_META[item.outcome].tone}>{OUTCOME_META[item.outcome].label}</StatusPill>
            : <OtherText grey>—</OtherText>),
    },
    {
        id: 'investigation',
        header: 'INVESTIGATION',
        minWidth: '18rem',
        cell: (item) => (
            <LossInvestigationCell>
                <StatusPill status={STATUS_TONE[item.status]} dot={item.status === 'Under investigation' && item.severity === 'critical'}>
                    {item.status}
                </StatusPill>
                {item.note && <OtherText color={tokens.text.muted}>{item.note}</OtherText>}
            </LossInvestigationCell>
        ),
    },
]

export const FuelLosses = () => {
    const dispatch = useDispatch()
    const [params, setParams] = useState<any>(getParams({ location }))
    const { fuelLosses } = useSelector((store: RootState) => store.fuelManagement)
    const [selectedLossId, setSelectedLossId] = useState<number | null>(null)

    useQueryPagination({ fetchFunctions: [getFuelLosses], cleanUpFunctions: [clearFuelLosses], params })

    const rows: IFuelOpsLossRow[] = useMemo(
        () => (fuelLosses?.data?.length ? fuelLosses.data.map(mapFuelLoss) : []),
        [fuelLosses],
    )

    const criticalLoss = findOpenCriticalLoss(rows)
    const selectedLoss = rows.find((row) => row.id === selectedLossId) || null

    const handleApproveWriteOff = async (loss: IFuelOpsLossRow) => {
        try {
            await dispatch(approveFuelLossWriteOff(loss.id) as any)
            await dispatch(getFuelLosses(params) as any)
            setSelectedLossId(null)
        } catch {
            toast.error('Could not approve write-off')
        }
    }

    const handleCloseWrittenOff = async (loss: IFuelOpsLossRow) => {
        try {
            await dispatch(
                updateFuelLossStatus({
                    id: loss.id,
                    body: {
                        caseStatus: FuelLossCaseStatus.Closed,
                        outcome: FuelLossOutcome.WrittenOff,
                    },
                }) as any,
            )
            await dispatch(getFuelLosses(params) as any)
            setSelectedLossId(null)
        } catch {
            toast.error('Could not close loss case')
        }
    }

    const handleRequestInvestigation = async (loss: IFuelOpsLossRow) => {
        try {
            await dispatch(
                updateFuelLossStatus({
                    id: loss.id,
                    body: { caseStatus: FuelLossCaseStatus.Open },
                }) as any,
            )
            await dispatch(getFuelLosses(params) as any)
            toast.info(`Case returned for investigation — owner ${loss.owner} notified`)
            setSelectedLossId(null)
        } catch {
            toast.error('Could not update case')
        }
    }

    return (
        <>
            <PageWrapper>
                <NavBar />
                <Flex between mb='16' >
                    <Flex column gap='0.25rem'>
                        <MainHeadline>Fuel Losses</MainHeadline>
                        <OtherText grey>Residual variances by severity, cost, closure status and outcome</OtherText>
                    </Flex>
                </Flex>
                {criticalLoss && (
                    <AlarmBanner style={{ marginTop: 0, marginBottom: '1rem' }}>
                        <AlarmContent>
                            <AlarmDot />
                            <AlarmText>{criticalLossBannerText(criticalLoss)}</AlarmText>
                        </AlarmContent>
                    </AlarmBanner>
                )}
                {!rows.length ? (
                    <NoItems title='Fuel Losses' />
                ) : (
                    <>
                        <HonestTable
                            columns={columns}
                            data={rows}
                            keyField='id'
                            onClick={(item) => setSelectedLossId(item.id)}
                        />
                        <LossExplainerWrap>
                            <OtherText color={tokens.text.muted}>{FUEL_LOSS_CLOSURE_CAPTION}</OtherText>
                            <OtherText color={tokens.text.muted}>{FUEL_RESIDUAL_EXPLAINER}</OtherText>
                        </LossExplainerWrap>
                    </>
                )}
                {selectedLoss && (
                    <LossCaseModal
                        loss={selectedLoss}
                        closeModal={() => setSelectedLossId(null)}
                        onApproveWriteOff={handleApproveWriteOff}
                        onCloseWrittenOff={handleCloseWrittenOff}
                        onRequestInvestigation={handleRequestInvestigation}
                    />
                )}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams)}
                    pageCount={Math.ceil(fuelLosses.totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        </>
    )
}
